import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useInfiniteQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import {
  autoResumeUploads,
  cancelUpload,
  getUploadsSnapshot,
  isUploadActive,
  onUploadSettled,
  resumableAssetIds,
  resumeUpload,
  retryUpload,
  startUpload,
  subscribeUploads,
  type LocalUpload,
} from "./uploadManager";
import { deleteUpload, getUploadStatus, listUploads, type UploadRow, type UploadsPage } from "./uploadsApi";
import { TYPE_COLOR } from "./schedulerUtils";
import type { ContentItem } from "./types";

const QUERY_KEY = ["v2", "channel-schedule", "uploads"] as const;
/** How often the list refreshes while a video is still being transcoded. */
export const UPLOADS_POLL_MS = 10_000;

/** A server upload row as a library item. The "up-" id prefix is what the
 * save path turns back into contentType "upload" (see apiMapping). Uploads
 * know their exact runtime, so drops never enter the pending-probe state,
 * and they may air on any channel (no channelId lock, unlike games). */
export function uploadToContent(row: UploadRow): ContentItem {
  return {
    id: `up-${row.assetId}`,
    title: row.title,
    type: "upload",
    league: "",
    thumbnail: row.thumbnail ?? "",
    durationSec: row.durationSec ?? 0,
    durationEstimated: row.durationSec == null,
    date: (row.createdAt || "").slice(0, 10),
    uploadStatus: row.status,
    failureReason: row.failureReason,
    source: {
      assetId: row.assetId,
      description: row.description,
      fileName: row.fileName,
      sizeBytes: row.sizeBytes,
      color: TYPE_COLOR.upload,
    },
  };
}

/**
 * The Uploaded tab's data: the server's upload list (polled while anything
 * is transcoding), the in-browser upload progress from the module-level
 * manager, and the actions. `items` are the library cards; only ready ones
 * are draggable onto the timeline.
 */
export function useUploads(search = "") {
  const queryClient = useQueryClient();
  const local = useSyncExternalStore(subscribeUploads, getUploadsSnapshot, getUploadsSnapshot);

  // Paged like the games feed: the title search runs on the server and the
  // grid asks for the next page as it scrolls (see useInfiniteGrid).
  const query = useInfiniteQuery({
    queryKey: [...QUERY_KEY, search],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const page = await listUploads(search, pageParam);
      // The per-asset status endpoint reconciles a processing row straight
      // from MediaConvert when its completion callback never arrived; the
      // list endpoint does not. Asking it for every processing row makes
      // the poll itself the recovery path, so a lost callback cannot leave
      // a card spinning forever.
      const uploads = await Promise.all(
        page.uploads.map(async (row) => {
          if (row.status !== "processing") return row;
          try {
            return { ...row, ...(await getUploadStatus(row.assetId)) };
          } catch {
            return row;
          }
        }),
      );
      return { ...page, uploads };
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextOffset : undefined),
    refetchInterval: (q) => {
      const pages = (q.state.data as InfiniteData<UploadsPage> | undefined)?.pages ?? [];
      return pages.some((p) => p.uploads.some((r) => r.status === "pending" || r.status === "processing"))
        ? UPLOADS_POLL_MS
        : false;
    },
  });

  const refresh = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    [queryClient],
  );

  // A finished (or failed) browser upload changes the server row.
  useEffect(() => onUploadSettled(refresh), [refresh]);

  // Interrupted uploads (reload, closed tab) pick themselves back up from
  // the File kept in IndexedDB; nothing for the admin to do.
  useEffect(() => {
    void autoResumeUploads();
  }, []);

  // Closing the tab mid-upload loses nothing (the file resumes later), but
  // the browser should still ask first, the way YouTube's uploader does.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!isUploadActive()) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, []);

  const rows = useMemo(
    () => query.data?.pages.flatMap((p) => p.uploads) ?? [],
    [query.data],
  );
  const resumable = useMemo(() => new Set(resumableAssetIds()), [rows, local]);
  const items = useMemo(() => rows.map(uploadToContent), [rows]);

  const start = useCallback(
    async (file: File, title: string, description: string | null) => {
      await startUpload(file, title, description);
      refresh();
    },
    [refresh],
  );

  const resume = useCallback(
    async (assetId: string, file: File, title: string) => {
      await resumeUpload(assetId, file, title);
    },
    [],
  );

  /** Resume from the stored file; false when this browser has none, and
   *  the caller should ask for the file (then call resume). */
  const retry = useCallback((assetId: string) => retryUpload(assetId), []);

  const cancel = useCallback(
    async (assetId: string) => {
      await cancelUpload(assetId);
      refresh();
    },
    [refresh],
  );

  const { fetchNextPage: fetchNext } = query;
  const fetchNextPage = useCallback(() => void fetchNext(), [fetchNext]);

  const remove = useCallback(
    async (assetId: string) => {
      await deleteUpload(assetId);
      refresh();
    },
    [refresh],
  );

  return {
    rows,
    items,
    /** In-browser progress by asset id (only uploads running in this tab). */
    local: local as Record<string, LocalUpload>,
    /** Asset ids this browser can resume (it holds their part ETags). */
    resumable,
    isLoading: query.isLoading,
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage,
    start,
    resume,
    retry,
    cancel,
    remove,
    refresh,
  };
}

export type UploadsState = ReturnType<typeof useUploads>;
